import type {
  LightboxGalleryInterface,
  Product,
  ProductCardProps,
  ProductDetailFeaturesAttribute as Attribute,
  MediaItemInterface,
  HyperlinkInterface,
  NavigationItemInterface,
  BadgeStyle
} from '@hypercodestudio/basler-components';

import {
  getCustomAttributeArrayValue,
  getCustomAttributeFirstValue,
  getCustomAttributeValue
} from './getCustomAttributeValueFromMetadataCode';
import { getProductImage } from './getProductImage';
import type { GenerateImageOptions } from '~/utils/generateImageObject';
import {
  PDP_GALLERY_SECONDARY_THUMB_HEIGHT,
  PDP_GALLERY_SECONDARY_THUMB_WIDTH,
  buildProductListImageOptions
} from '~/utils/imagePresetRegistry';
import type {
  AttributeSetFragment,
  CommonProductDetailFragment,
  CommonProductFragment,
  ConfigurableProduct,
  CrossSellProductFragment,
  CustomAttributeV2Fragment,
  ProductAttributeFilterInput,
  ProductDetailFragment,
  ProductInterface
} from '~~/lib/Shop/generated/schema';
import {
  ACCESSORY_CATEGORY,
  ACCESSORY_TYPE,
  BADGE_REFERENCE_DATA,
  BRAND,
  BRAND_DATA,
  CAMERA_CATEGORY,
  CAMERA_PRODUCT_LINE,
  CAMERA_SERIES_CODE,
  CHANNEL_PARTNERS,
  CHANNEL_PARTNERS_DATA,
  COMING_SOON_LABEL,
  CONFORMITY_ENTITY_DATA,
  DISCONTINUATION_ADDITIONAL_LINKS_DATA,
  DISCONTINUATION_STATUS,
  DISCONTINUATION_STATUS_DISCONTINUED,
  DISTRIBUTORS,
  DISTRIBUTORS_DATA,
  DOCUMENTATION_AVAILABLE,
  DOCUMENTATION_URL,
  DOCUMENTATION_URL_SLUG,
  ECOMMERCE_SHOW_ADDTOCARTBUTTON,
  ECOMMERCE_SHOW_REQUESTCARTBUTTON,
  FAMILY,
  FASTDELIVERY_AVAILABLE,
  EMVA_SPECTRAL_SENSITIVITY_ABS_DATA,
  TRANSMISSION_GRAPH,
  HARDWARE_FEATURES_DATA,
  FEATURES_FRAMEGRABBER_DATA,
  INTERFACE,
  IS_IN_BUNDLE,
  LAST_TIME_BUY,
  LAST_TIME_SERVICE,
  META_DESCRIPTION,
  MODEL_NAME,
  PDP_ACTION_BOX_LINKS_DATA,
  SEO_FOLLOW,
  SEO_HEADLINE,
  SEO_INDEX,
  SENSOR_DESCRIPTION,
  SHIPPING_TIME_CODE,
  SHOW_LS,
  SHOW_PRICE,
  SHOW_VSC,
  STATE_IN_STOCK,
  TEASER_TEXT,
  TECHNICAL_DRAWING,
  TESTCAMERA_AVAILABLE
} from '~/utils/shop/constants';
import {
  createSpecifications,
  extractLabelValueFromCustomAttribute,
  flipAttributeSetMapping
} from '~/utils/shop/createSpecifications';
import type {
  SpecificationInlineLink,
  SpecificationGroup
} from '~/utils/shop/specTableValueWithLink';
import { getHighlightAttributesForAttributeSet } from '~/utils/shop/booleanAttributeMetadata';
import { isBooleanValue } from '~/utils/shop/guards/isBooleanValue';
import { isDefined } from '~/utils/guards/isDefined';
import { createForwardedValues } from '~/utils/shop/createForwardedValues';
import type { ProductFiltersModel } from '~/stores/productFilterStore/model';
import { createShippingTimeText } from '~/utils/shop/createShippingTimeText';
import { createPriceText } from '~/utils/shop/createPriceText';
import type { PdpConfiguration } from '~/composables/usePdpConfiguration';
import type { LOCALE_CODE } from '~~/lib/ContentfulService';
import { failSafeParseJSON } from '~/utils/failSafeParseJSON';

import { ensureArray } from '~/utils/ensureArray';
import { extractCustomAttributes } from '~/utils/shop/extractCustomAttributes';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';

import {
  isDateString,
  isDateTimeString,
  mapToFormattedDate
} from '~/utils/mapper/mapToFormattedDate';

export type RelatedProductGroupModel = {
  tabId: string;
  title: string;
  groupTitle: string;
  queryFilter: ProductAttributeFilterInput;
  products: (CommonProductFragment | CommonProductDetailFragment)[];
};

type ProductFeatureAttributeModel = {
  label?: { value: string };
  link: { value: string };
  camfeature_description?: { value: string };
  featuregruppe: { value: string };
  magento_option_id: string;
};

type BadgeReferenceData = {
  badgetext?: { value?: string[] };
  badgestyle?: { value?: string[] };
  hasshineanimation?: { value?: string[] };
};

export type ProductBadge = {
  badgeText: string;
  badgeStyle: BadgeStyle | '';
  hasShineAnimation: boolean;
  iconType?: string;
  iconName?: string;
};

export type ProductEnrichmentRules = {
  productSkus?: string[];
  productUrlKeys?: string[];
  productSeries?: string[];
  cameraProductFamily?: string[];
  productAccessoryCategories?: string[];
  accessoryType?: string[];
  productCategories?: string[];
  categoryIds?: string[];
  categoryKeys?: string[];
};

type PdpActionBoxLink = {
  ctf_dict_key: {
    label: string;
    value: string[];
  };
  ctf_entity_id: {
    label: string;
    value: string[];
  };
  label: {
    label: string;
    value: string[];
  };
  magento_option_id: string;
  external_url: {
    label: string;
    value: string[];
  };
};

/**
 * XXX: Create a model for List Use and one for PDP use.
 *  They might share the same base object to reuse properties.
 */
export class DecoratedProduct {
  // XXX: all properties should be public and readonly, remove explicit getters.
  public readonly id: string;
  public readonly sku: string | undefined;
  public readonly urlKey: string | undefined;
  /**
   * The "complete" name (might including series. etc.)
   *
   * @example "Stereo Camera rc_visard 160c"
   */
  public readonly name: string | undefined;
  /**
   * The shipping time in days.
   */
  public readonly shippingTime: number;
  public readonly shippingTimeText: string | undefined;
  public readonly modelName: string | undefined;
  public readonly category: string | undefined;
  public readonly categoryId: string | undefined;
  public readonly categories:
    | ({
        __typename?: 'CategoryTree' | undefined;
        uid: string;
        id?: number | null | undefined;
        name?: string | null | undefined;
        category_key?: string | null | undefined;
        breadcrumb_name?: string | null | undefined;
        contentful_page_id?: string | null | undefined;
      } | null)[]
    | null;
  public readonly cameraCategory: string | undefined;
  public readonly productBrand: string | undefined;
  public readonly productFeaturesAttributes: ReadonlyArray<ProductFeatureAttributeModel>;
  /**
   * @deprecated
   */
  public readonly distributors: string[] | undefined;
  /**
   * @deprecated
   */
  public readonly distributorsData: string[] | undefined;
  public readonly channelPartners: string[] | undefined;
  public readonly channelPartnersData: {
    deepurl: {
      label: string;
      value: string[];
    };
    image: {
      label: string;
      value: string[];
    };
    label: {
      label: string;
      value: string[];
    };
    logourl: {
      label: string;
      value: string[];
    };
    magento_option_id: string;
  }[];
  public readonly productDescription: string | undefined;
  public readonly productDocumentationLink: string | undefined;
  public readonly productDocumentationLinkSlug: string | undefined;
  public readonly productTeaserText: string | undefined;
  public readonly sensorDescriptionText: string | undefined;
  public readonly seriesLabel: string | undefined;
  public readonly productCurrency: string | undefined;
  public readonly metaTitle: string | undefined;
  public readonly metaDescription: string | undefined;
  public readonly isSale: boolean = false;
  public readonly hasFastDelivery: boolean = false;
  public readonly badge: ProductBadge;
  public readonly currencySign: string | undefined;
  public readonly series: string;
  public readonly family: string;
  public readonly familyLabel: string | undefined;
  public readonly price: number | undefined;
  public readonly discountPrice: number | undefined;
  public readonly discount: number | undefined;
  public readonly discountPercent: number | undefined;
  public readonly pdpActionBoxLinksData: PdpActionBoxLink | undefined;
  public readonly priceText: string;
  public readonly previousPriceText: string | undefined;
  public readonly pricePrefix: string;
  public readonly pricePrefixAttentionText: string | undefined;
  public readonly isOutOfStock: boolean;
  public readonly isVisionSystemConfigurable: boolean;
  public readonly isInLensSelector: boolean;
  public readonly isConfigurableProduct: boolean;
  public readonly showPrice: boolean;
  public readonly showAddToCartButton: boolean;
  public readonly showRequestCartButton: boolean;
  public readonly showVSC: boolean;
  public readonly showShippingTime: boolean;
  public readonly brandData: {
    image: string[];
    label: {
      label: string;
      value: string[];
    };
    magento_option_id: string;
  }[];
  public readonly conformityEntityData: [];
  public readonly interface?: string;
  public readonly testCameraAvailable: boolean;
  public readonly productLine: string | undefined;
  public readonly seoIndex: string | undefined;
  public readonly seoFollow: string | undefined;
  public readonly technicalDrawing: TechnicalDrawing;
  public readonly emvaSpectralSensitivityData: [];
  public readonly transmissionGraphData: [];
  public readonly discontinuationStatus: string | undefined;
  public readonly followingProducts: CrossSellProductFragment[];
  public readonly discontinuationLinks: {
    ctf_entity_id?: {
      label: string;
      value: string[];
    };
    parameters?: {
      label: string;
      value: string[];
    };
    magento_option_id?: string;
  }[];
  public readonly accessoryCategory: string | undefined;
  public readonly accessoryType: string | undefined;
  /**
   * XXX: do we want to expose custom attributes?
   */
  public readonly customAttributes: ReadonlyArray<CustomAttributeV2Fragment>;
  public readonly isDiscontinued: boolean;
  public readonly bundleUrlKey: string | undefined;

  private readonly mappedCustomAttributes: IndexedCustomAttributes;

  static build(
    product:
      | (CommonProductFragment & Partial<ProductDetailFragment>)
      | (CommonProductDetailFragment & Partial<ProductDetailFragment>)
      | (ProductInterface & Partial<ProductDetailFragment>),
    translations: Partial<Record<string, string>> = {},
    locale = 'en-us',
    isTextTeaserCardList = false
  ) {
    const p = product as unknown as CommonProductFragment;
    return new DecoratedProduct(p, translations, locale, isTextTeaserCardList);
  }

  /**
   * TODO: move most of this to server side.
   * Make independent from Translations and create a class on client for
   * list and detail page use.
   */
  constructor(
    /**
     * @deprecated Direct access to the product should not be necessary.
     * Derive a property instead!
     */
    public readonly product:
      | (CommonProductFragment & Partial<ProductDetailFragment>)
      | (CommonProductDetailFragment & Partial<ProductDetailFragment>),
    private readonly translations: Partial<Record<string, string>>,
    private readonly locale: string,
    private readonly isTextTeaserCardList: boolean
  ) {
    this.id = product.uid;
    this.urlKey = product.url_key ?? undefined;
    this.sku = product.sku ?? undefined;
    this.isOutOfStock = product.stock_status !== STATE_IN_STOCK;

    this.customAttributes = extractCustomAttributes(product);

    this.mappedCustomAttributes = getIndexCustomAttributes(
      this.customAttributes
    );

    this.isVisionSystemConfigurable = extractBool(
      getCustomAttributeValue(this.mappedCustomAttributes[SHOW_VSC], {
        label: false
      }),
      false
    );

    this.isInLensSelector = extractBool(
      getCustomAttributeValue(this.mappedCustomAttributes[SHOW_LS], {
        label: false
      }),
      false
    );

    this.showAddToCartButton = extractBool(
      getCustomAttributeValue(
        this.mappedCustomAttributes[ECOMMERCE_SHOW_ADDTOCARTBUTTON],
        { label: false }
      ),
      true
    );

    this.showRequestCartButton = extractBool(
      getCustomAttributeValue(
        this.mappedCustomAttributes[ECOMMERCE_SHOW_REQUESTCARTBUTTON],
        { label: false }
      ),
      true
    );

    this.shippingTime = getCustomAttributeValue(
      this.mappedCustomAttributes[SHIPPING_TIME_CODE]
    )
      ? extractNumber(
          getCustomAttributeValue(
            this.mappedCustomAttributes[SHIPPING_TIME_CODE]
          ),
          0
        )
      : 0;

    this.productFeaturesAttributes = failSafeParseJSON(
      this.mappedCustomAttributes[HARDWARE_FEATURES_DATA]
        ? getCustomAttributeValue(
            this.mappedCustomAttributes[HARDWARE_FEATURES_DATA]
          )
        : this.mappedCustomAttributes[FEATURES_FRAMEGRABBER_DATA]
          ? getCustomAttributeValue(
              this.mappedCustomAttributes[FEATURES_FRAMEGRABBER_DATA]
            )
          : '',
      []
    );

    this.distributors = getCustomAttributeArrayValue(
      this.mappedCustomAttributes[DISTRIBUTORS]
    );
    this.distributorsData = getCustomAttributeArrayValue(
      this.mappedCustomAttributes[DISTRIBUTORS_DATA]
    );

    this.channelPartners = getCustomAttributeArrayValue(
      this.mappedCustomAttributes[CHANNEL_PARTNERS]
    );

    this.channelPartnersData = failSafeParseJSON<[], []>(
      getCustomAttributeValue(
        this.mappedCustomAttributes[CHANNEL_PARTNERS_DATA]
      ),
      []
    );

    this.productDescription = getCustomAttributeValue(
      this.mappedCustomAttributes[SEO_HEADLINE]
    );

    this.technicalDrawing = new TechnicalDrawing(
      (() => {
        const technicalDrawing = Object.values(
          failSafeParseJSON<{}, {}>(
            getCustomAttributeValue(
              this.mappedCustomAttributes[TECHNICAL_DRAWING]
            ),
            {}
          )
        )[0] as {} | null;

        if (JSON.stringify(technicalDrawing) === '{}') {
          return null;
        }
        return technicalDrawing;
      })()
    );

    this.badge = parseBadgeReferenceData(
      getCustomAttributeValue(
        this.mappedCustomAttributes[BADGE_REFERENCE_DATA]
      ),
      this.mappedCustomAttributes,
      this.locale
    );

    this.hasFastDelivery = getFastDelivery(
      product,
      this.mappedCustomAttributes
    );

    this.productBrand = getCustomAttributeValue(
      this.mappedCustomAttributes[BRAND]
    );

    this.productTeaserText = getCustomAttributeValue(
      this.mappedCustomAttributes[TEASER_TEXT]
    );

    this.sensorDescriptionText = getCustomAttributeValue(
      this.mappedCustomAttributes[SENSOR_DESCRIPTION]
    );

    this.bundleUrlKey = getCustomAttributeValue(
      this.mappedCustomAttributes[IS_IN_BUNDLE]
    );

    this.metaTitle = product.meta_title ?? undefined;

    this.metaDescription =
      getCustomAttributeValue(this.mappedCustomAttributes[META_DESCRIPTION]) ??
      product.meta_description ??
      undefined;

    this.name = product.name ?? undefined;

    const modelNameFromAttribute = getCustomAttributeValue(
      this.mappedCustomAttributes[MODEL_NAME]
    )?.trim();
    this.modelName = modelNameFromAttribute || (this.product.name as string);

    this.category = product.categories?.[0]?.name ?? undefined;
    this.categoryId = product.categories?.[0]?.uid ?? undefined;
    this.categories = product.categories ?? null;

    const isDocumentationAvailable = extractBool(
      getCustomAttributeValue(
        this.mappedCustomAttributes[DOCUMENTATION_AVAILABLE],
        {
          label: false
        }
      ),
      false
    );
    const documentationUrl = getCustomAttributeValue(
      this.mappedCustomAttributes[DOCUMENTATION_URL]
    );

    const documentationUrlSlug = getCustomAttributeValue(
      this.mappedCustomAttributes[DOCUMENTATION_URL_SLUG]
    );

    if (isDocumentationAvailable && documentationUrl) {
      this.productDocumentationLink = documentationUrl;
      this.productDocumentationLinkSlug = documentationUrlSlug;
    }

    this.seriesLabel = getCustomAttributeValue(
      this.mappedCustomAttributes[CAMERA_SERIES_CODE]
    );

    this.discontinuationStatus = getCustomAttributeValue(
      this.mappedCustomAttributes[DISCONTINUATION_STATUS],
      { label: true }
    );
    this.isDiscontinued =
      this.discontinuationStatus === DISCONTINUATION_STATUS_DISCONTINUED;

    this.brandData = failSafeParseJSON<[], []>(
      getCustomAttributeValue(this.mappedCustomAttributes[BRAND_DATA]),
      []
    );
    this.conformityEntityData = failSafeParseJSON<[], []>(
      getCustomAttributeValue(
        this.mappedCustomAttributes[CONFORMITY_ENTITY_DATA]
      ),
      []
    );
    this.discontinuationLinks = failSafeParseJSON<[], []>(
      getCustomAttributeValue(
        this.mappedCustomAttributes[DISCONTINUATION_ADDITIONAL_LINKS_DATA]
      ),
      []
    );
    this.pdpActionBoxLinksData = failSafeParseJSON<PdpActionBoxLink[]>(
      getCustomAttributeValue(
        this.mappedCustomAttributes[PDP_ACTION_BOX_LINKS_DATA]
      )
    )?.[0];

    this.followingProducts =
      this.product?.crosssell_products?.filter(isDefined) ?? [];

    this.interface = getCustomAttributeValue(
      this.mappedCustomAttributes[INTERFACE]
    );

    this.familyLabel = getCustomAttributeValue(
      this.mappedCustomAttributes[FAMILY]
    );

    this.cameraCategory = getCustomAttributeFirstValue(
      this.mappedCustomAttributes[CAMERA_CATEGORY]
    );

    this.accessoryCategory = getCustomAttributeFirstValue(
      this.mappedCustomAttributes[ACCESSORY_CATEGORY]
    );

    this.accessoryType = getCustomAttributeFirstValue(
      this.mappedCustomAttributes[ACCESSORY_TYPE]
    );

    this.emvaSpectralSensitivityData = this.parseChartData(
      EMVA_SPECTRAL_SENSITIVITY_ABS_DATA
    );

    this.transmissionGraphData = this.parseChartData(TRANSMISSION_GRAPH);

    this.series = this.seriesLabel ?? this.productBrand ?? '';
    this.family =
      this.familyLabel && this.productBrand
        ? this.productBrand + ' ' + this.familyLabel
        : this.series;
    this.isConfigurableProduct = 'configurable_options' in product;
    this.showVSC =
      this.isVisionSystemConfigurable && !this.isConfigurableProduct;

    this.showShippingTime = !(
      (this.shippingTime ?? 0) <= 0 ||
      this.shippingTime >= 180 ||
      this.isOutOfStock ||
      (!this.showAddToCartButton && !this.showRequestCartButton)
    );

    this.shippingTimeText = createShippingTimeText({
      shippingTime: this.shippingTime,
      showShippingTime: this.showShippingTime,
      fallbackLabel: translations['pdp.shippingTimeFallback.label'],
      labelTemplateMulti: translations['pdp.shippingTime.template.multi'],
      labelTemplateSingle: translations['pdp.shippingTime.template.single'],
      labelTemplateMultiDays:
        translations['pdp.shippingTime.template.multi.days']
    });

    // Price related things
    // e.g. "EUR"
    this.productCurrency =
      product?.price_range?.minimum_price?.final_price?.currency ?? '';
    if (this.productCurrency) {
      try {
        // the locale does not matter as only the sign is used.
        this.currencySign = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.productCurrency
        })
          .formatToParts(1)
          .find((part) => part.type === 'currency')?.value;
      } catch (_e) {
        // XXX: proper logging? until that, ignore error for now
      }
    }

    this.price =
      product.price_range?.minimum_price?.regular_price?.value ?? undefined;
    this.discountPrice =
      product.price_range?.minimum_price?.final_price?.value ?? undefined;
    this.discountPercent =
      product.price_range?.minimum_price?.discount?.percent_off ?? undefined;
    this.isSale =
      this.discountPrice != null && this.discountPrice !== this.price;

    this.showPrice = !(
      (this.price ?? 0) < 0.01 ||
      !extractBool(
        getCustomAttributeValue(this.mappedCustomAttributes[SHOW_PRICE], {
          label: false
        }),
        true
      )
    );

    this.discount =
      product.price_range?.minimum_price?.discount?.amount_off ?? undefined;

    this.priceText = this.isSale
      ? this.createPriceText(this.discountPrice)
      : this.createPriceText(this.price);
    this.previousPriceText = this.isSale
      ? this.createPriceText(this.price)
      : undefined;

    this.pricePrefixAttentionText =
      this.discountPercent && this.discountPercent > 1
        ? `-${this.discountPercent.toFixed(0)}%`
        : undefined;

    this.pricePrefix = translations['pdp.netPrice.label'] ?? '';

    this.testCameraAvailable = extractBool(
      getCustomAttributeValue(
        this.mappedCustomAttributes[TESTCAMERA_AVAILABLE],
        { label: false }
      ),
      false
    );

    this.productLine = getCustomAttributeValue(
      this.mappedCustomAttributes[CAMERA_PRODUCT_LINE]
    );

    this.seoIndex = getCustomAttributeValue(
      this.mappedCustomAttributes[SEO_INDEX]
    );
    this.seoFollow = getCustomAttributeValue(
      this.mappedCustomAttributes[SEO_FOLLOW]
    );
  }

  renderFeatureAttributes(): {
    key: string;
    items: ReadonlyArray<Attribute>;
  }[] {
    const features: Attribute[] = this.productFeaturesAttributes.map(
      (p) =>
        ({
          key: ensureArray(p.featuregruppe?.value)[0],
          label: ensureArray(p.link?.value)[0] ?? '',
          description: ensureArray(p.camfeature_description?.value)[0],
          value:
            this.translations[
              `pdp.feature.${ensureArray(p.label?.value)[0]}.label`
            ] ??
            ensureArray(p.label?.value)[0] ??
            ''
        }) satisfies Attribute
    );

    const groupedFeature: Record<string, Attribute[]> = features.reduce(
      (prevValue: { [key: string]: any }, attr) => ({
        ...prevValue,
        [attr.key]: [...(prevValue[attr.key] || []), attr]
      }),
      {}
    );

    const preparedForTable = Object.entries(groupedFeature)
      .map(([key, items]) => ({
        key,
        items: items.sort((a, b) =>
          a.value.toString().localeCompare(b.value.toString())
        )
      }))
      .sort((a: { key: string }, b: { key: string }) =>
        a.key.localeCompare(b.key)
      );

    return preparedForTable.filter(
      (x) => x.key !== 'undefined' && x.items.length !== 0
    );
  }

  getProductImage(
    options: GenerateImageOptions = {},
    defaultImageUrl?: string
  ): Extract<MediaItemInterface, { type: 'image' }> | undefined {
    return getProductImage(this.product, options, defaultImageUrl)[0]?.media;
  }

  private isSeoHeadlineVisibleInListEntry(): boolean {
    const productWithHighlight = this.product as typeof this.product & {
      highlight_attributes?: {
        seo_headline_visible?: boolean | null;
      } | null;
    };

    const cachedHighlightMetadata = getHighlightAttributesForAttributeSet(
      this.locale,
      this.product.attribute_set_id
    );

    return (
      productWithHighlight.highlight_attributes?.seo_headline_visible ??
      cachedHighlightMetadata?.seo_headline_visible ??
      false
    );
  }

  asListEntry(
    formValues: ProductFiltersModel,
    createDetailLink: (p: DecoratedProduct) => string,
    productListType:
      'Specifications' | 'Discontinued' | 'Plain' = 'Specifications',
    discontinuedSpecs: string[] = [],
    defaultProductImageUrl?: string,
    isFullSizeImage = false
  ): ProductCardProps {
    const listImageOptions = buildProductListImageOptions({
      isFullSizeImage,
      isTextTeaserCardList: this.isTextTeaserCardList
    });
    const attributes = this.buildAttributesForListEntry(
      productListType,
      discontinuedSpecs
    );

    const badge = this.buildBadge();

    return {
      showAttributes: true,
      link: this.createDetailPageLink(createDetailLink, formValues),
      badgeText: badge.badgeText,
      badgeStyle: badge.badgeStyle || undefined,
      hasShineAnimation: badge.hasShineAnimation,
      iconType: badge.iconType,
      iconName: badge.iconName,
      product: {
        id: this.id ?? '',
        sku: this.sku ?? undefined,
        name: this.series !== '' ? this.series : ' ',
        variantName: this.modelName ?? '',
        description:
          productListType === 'Discontinued'
            ? ''
            : this.isSeoHeadlineVisibleInListEntry()
              ? (this.productDescription ?? ' ')
              : ' ',
        // XXX: wird nirgendwo angezeigt?
        price: this.discountPrice ?? this.price ?? -1,
        priceText: productListType !== 'Discontinued' ? this.priceText : '',
        pricePrefixAttentionText: this.showPrice
          ? this.pricePrefixAttentionText
          : undefined,
        previousPriceText: this.showPrice ? this.previousPriceText : undefined,
        pricePrefix: productListType !== 'Discontinued' ? this.pricePrefix : '',
        attributes,
        media: this.getProductImage(
          {
            ...listImageOptions
          },
          defaultProductImageUrl
        ) ?? { type: 'image', content: { src: '' } }
      } satisfies Product
    };
  }

  /**
   * Builds badge information including text, style, and shine animation.
   * Returns an object with badgeText, badgeStyle, and hasShineAnimation.
   */
  buildBadge(): ProductBadge {
    return this.badge;
  }

  createPriceText(overwritePrice = 0): string {
    return createPriceText({
      showPrice: this.showPrice,
      locale: this.locale,
      price: this.product?.price_range?.minimum_price?.final_price,
      overwritePrice,
      onRequestLabel: this.translations['pdp.priceOnRequest.label'],
      prefix:
        'variants' in this.product && this.product.variants
          ? this.translations['pdp.netPrice.variantPrefix'] + ' '
          : undefined
    });
  }

  /**
   * Extracts highlighted attributes from the product's highlight_attributes field.
   * Returns attributes sorted by position.
   */
  getHighlightedAttributes(
    context: 'highlighted' | 'highlightedInList' = 'highlighted'
  ): Attribute[] {
    // Check if product has highlight_attributes field
    const productWithHighlight = this.product as typeof this.product & {
      highlight_attributes?: {
        attributes?: Array<{
          attribute_code?: string | null;
          icon_code?: string | null;
          position?: number | null;
        } | null> | null;
      } | null;
    };

    const cachedHighlightMetadata = getHighlightAttributesForAttributeSet(
      this.locale,
      this.product.attribute_set_id
    );
    const highlightAttributesFromProduct =
      productWithHighlight.highlight_attributes?.attributes;
    const highlightAttributesFromCache =
      cachedHighlightMetadata?.attributes?.map((attribute) => ({
        attribute_code: attribute.attribute_code,
        icon_code: attribute.icon_code,
        position: attribute.position
      }));
    const highlightAttributesSource =
      highlightAttributesFromProduct ?? highlightAttributesFromCache;

    if (!highlightAttributesSource) {
      return [];
    }

    type HighlightAttribute = {
      attribute_code?: string | null;
      icon_code?: string | null;
      position?: number | null;
    };
    const highlightAttributes = highlightAttributesSource.filter(
      (highlightAttr): highlightAttr is HighlightAttribute =>
        isDefined(highlightAttr)
    );

    if (highlightAttributes.length === 0) {
      return [];
    }

    const indexedCustomAttributes = getIndexCustomAttributes(
      this.customAttributes
    );

    type HighlightAttributeWithPosition = Attribute & { position: number };

    return highlightAttributes
      .map((highlightAttr): HighlightAttributeWithPosition | undefined => {
        if (!highlightAttr) {
          return undefined;
        }

        const attributeCode = highlightAttr.attribute_code;
        if (!attributeCode) {
          return undefined;
        }

        const customAttribute = indexedCustomAttributes[attributeCode];
        if (!customAttribute) {
          return undefined;
        }

        const { label, value } = extractLabelValueFromCustomAttribute(
          customAttribute,
          this.locale as LOCALE_CODE,
          this.product.attribute_set?.filter(isDefined) ?? []
        );

        if (isBooleanValue(customAttribute)) {
          if (context === 'highlighted') {
            if (customAttribute.value !== '1') {
              return undefined;
            }

            return {
              label,
              value: '',
              key: highlightAttr.icon_code ?? attributeCode,
              position: highlightAttr.position ?? 0
            } as HighlightAttributeWithPosition;
          }

          if (!value || (typeof value === 'string' && value.trim() === '')) {
            return undefined;
          }

          return {
            label,
            value,
            key: attributeCode,
            position: highlightAttr.position ?? 0
          } as HighlightAttributeWithPosition;
        }

        // Filter out attributes without a value (null, undefined, empty string, or only whitespace)
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          return undefined;
        }

        return {
          value,
          label,
          key:
            context === 'highlightedInList'
              ? attributeCode
              : (highlightAttr.icon_code ?? attributeCode),
          position: highlightAttr.position ?? 0
        } as HighlightAttributeWithPosition;
      })
      .filter(isDefined)
      .sort(
        (
          a: HighlightAttributeWithPosition,
          b: HighlightAttributeWithPosition
        ) => {
          const posA = a?.position ?? 0;
          const posB = b?.position ?? 0;
          return posA - posB;
        }
      )
      .map((attr: HighlightAttributeWithPosition): Attribute => {
        const { position, ...rest } = attr;
        return rest as Attribute;
      });
  }

  buildAttributesForListEntry(
    productListType: 'Specifications' | 'Discontinued' | 'Plain',
    discontinuedSpecCodes: string[]
  ) {
    if (
      productListType === 'Discontinued' &&
      discontinuedSpecCodes.length > 0
    ) {
      const indexedCustomAttributes = getIndexCustomAttributes(
        this.customAttributes
      );

      return discontinuedSpecCodes
        .map((attributeCode) => {
          const customAttribute = indexedCustomAttributes[attributeCode];
          if (!customAttribute) {
            return undefined;
          }

          const { label, value, key } = extractLabelValueFromCustomAttribute(
            customAttribute,
            this.locale as LOCALE_CODE,
            this.product.attribute_set?.filter(isDefined) ?? []
          );

          if (!isDefined(value) || String(value).trim() === '') {
            return undefined;
          }

          return {
            key: key ?? attributeCode,
            label,
            value
          } as Attribute;
        })
        .filter(isDefined);
    }

    // Use highlight_attributes from GraphQL for non-discontinued lists
    return this.getHighlightedAttributes('highlightedInList');
  }

  createDetailPageLink(
    createProductDetailLink: (p: DecoratedProduct) => string,
    filters: ProductFiltersModel
  ): HyperlinkInterface {
    const forwardedValues = createForwardedValues(filters);
    return {
      uri: {
        path: createProductDetailLink(this),
        query: Object.keys(forwardedValues)
          .filter((key: string) => key.startsWith('utm_'))
          .reduce(
            (obj: Record<string, any>, key) => {
              obj[key] = forwardedValues[key];
              return obj;
            },
            {} as Record<string, any>
          )
      },
      // title is missing in the link type
      ...{ title: this.name },
      external: false
    };
  }

  isBuyable(): boolean {
    return this.showPrice && this.showAddToCartButton && !this.isOutOfStock;
  }

  /**
   * PDP Only
   */
  getProductSpecifications(
    link?: SpecificationInlineLink
  ): SpecificationGroup[] {
    if (this.isDiscontinued) {
      const filteredAttributeSet =
        this.product.attribute_set?.filter(
          (as) =>
            isDefined(as) &&
            ((as as any).group?.attribute_group_code == 'general_information' ||
              (as as any).group?.attribute_group_code === 'general-information')
        ) ?? [];

      return createSpecifications(
        filteredAttributeSet as AttributeSetFragment[],
        this.customAttributes,
        'table',
        this.locale as LOCALE_CODE
      );
    }

    const chartAttributeCodes = [
      EMVA_SPECTRAL_SENSITIVITY_ABS_DATA,
      TRANSMISSION_GRAPH
    ] as const;

    const groups = createSpecifications(
      (this.product.attribute_set?.filter(isDefined) ??
        []) as AttributeSetFragment[],
      this.customAttributes,
      'table',
      this.locale as LOCALE_CODE,
      link,
      chartAttributeCodes
    );

    const chartConfigs = [
      {
        data: this.emvaSpectralSensitivityData,
        parentGroupCode: 'emva_data',
        titleKey: 'pdp.specs.emvaChart.label',
        titleFallback: 'EMVA Spectral Sensitivity',
        xTitleKey: 'pdp.specs.emvaChart.xTitleText',
        xTitleFallback: 'Wavelength λ [nm]',
        yTitleKey: 'pdp.specs.emvaChart.yTitleText',
        yTitleFallback: 'QuantumEfficiency %'
      },
      {
        data: this.transmissionGraphData,
        parentGroupCode: 'general_information',
        titleKey: 'pdp.specs.transmissionGraph.label',
        titleFallback: 'Transmission Graph',
        xTitleKey: 'pdp.specs.transmissionGraph.xTitleText',
        xTitleFallback: 'Wavelength λ [nm]',
        yTitleKey: 'pdp.specs.transmissionGraph.yTitleText',
        yTitleFallback: 'Transmission %'
      }
    ];

    if (import.meta.client) {
      for (const config of chartConfigs) {
        if (config.data.length > 0) {
          const chartGroup: SpecificationGroup = {
            type: 'charts',
            title: this.translations[config.titleKey] ?? config.titleFallback,
            charts: [
              {
                mediaContent: {
                  media: {
                    chartData: [
                      {
                        label: this.name ?? this.sku ?? '',
                        data: config.data as { x: number; y: number }[]
                      }
                    ],
                    xTitleText:
                      this.translations[config.xTitleKey] ??
                      config.xTitleFallback,
                    yTitleText:
                      this.translations[config.yTitleKey] ??
                      config.yTitleFallback,
                    yAxisMin: 0,
                    yAxisMax: 100
                  }
                }
              }
            ]
          };

          const parentGroup = (
            this.product.attribute_set?.filter(isDefined) ?? []
          ).find(
            (as) =>
              (as as AttributeSetFragment).group?.attribute_group_code ===
              config.parentGroupCode
          ) as AttributeSetFragment | undefined;
          const parentGroupName = parentGroup?.group?.attribute_group_name;
          const parentGroupIndex = parentGroupName
            ? groups.findIndex((g) => g.title === parentGroupName)
            : -1;

          if (parentGroupIndex !== -1) {
            groups.splice(parentGroupIndex + 1, 0, chartGroup);
          } else {
            groups.push(chartGroup);
          }
        }
      }
    }

    if (this.technicalDrawing.isValid()) {
      const altText = [
        this.translations['pdp.specs.technical_drawing.label'],
        this.name
      ]
        .filter(isDefined)
        .join(' ')
        .trim();

      groups.push({
        type: 'images',
        title:
          this.translations['pdp.specs.technical_drawing.label'] ??
          'Technical Drawing',
        images: [
          {
            mediaContent: {
              lazyload: true,
              cols: [12, 3],
              media: {
                sizes: '',
                mimeType: 'image/svg',
                src: this.technicalDrawing.getTechnicalDrawingLink() ?? '',
                srcset: this.technicalDrawing.getTechnicalDrawingLink(),
                alt:
                  altText ||
                  this.technicalDrawing.getLabel() ||
                  'Technical Drawing',
                width: PDP_GALLERY_SECONDARY_THUMB_WIDTH,
                height: PDP_GALLERY_SECONDARY_THUMB_HEIGHT
              }
            },
            lightboxIndex: 0,
            downloadLink: this.technicalDrawing.getCadLink() ?? '',
            downloadLinkText:
              this.translations[
                'pdp.specs.technical_drawing.cad_download_link.label'
              ] ?? 'Download CAD File'
          }
        ]
      });
    }

    if (this.brandData.length > 0) {
      const brandData = this.brandData.filter(isDefined);

      groups.push({
        type: 'table',
        title: this.translations['pdp.specs.brand.label'] ?? 'Brand',
        specs: [
          {
            label: ensureArray(brandData[0]?.label?.value)[0],
            value: ''
          }
        ]
      });
    }

    if (this.conformityEntityData.length > 0) {
      groups.push({
        type: 'checklist',
        title: this.translations['pdp.specs.conformity.label'] ?? 'Conformity',
        // @ts-expect-error Here is an Error in the interface of basler-components
        items: this.conformityEntityData
          .map(
            (x: Pick<PdpActionBoxLink, 'label'> | undefined) =>
              ensureArray(x?.label?.value)[0] ?? undefined
          )
          .filter(isDefined)
      });
    }

    return groups;
  }

  /**
   * PDP Only
   */
  getProductSpecificationsLightboxGallery(): LightboxGalleryInterface['mediaList'] {
    const lightboxGallery: LightboxGalleryInterface['mediaList'] = [];

    if (this.technicalDrawing.isValid()) {
      const altText = [
        this.translations['pdp.specs.technical_drawing.label'],
        this.name
      ]
        .filter(isDefined)
        .join(' ')
        .trim();

      lightboxGallery.push({
        lazyload: false,
        cols: [12, 3],
        media: {
          type: 'image',
          content: {
            src: this.technicalDrawing.getTechnicalDrawingLink() ?? '',
            srcset: this.technicalDrawing.getTechnicalDrawingLink(),
            alt:
              altText ||
              this.technicalDrawing.getLabel() ||
              'Technical Drawing',
            width: PDP_GALLERY_SECONDARY_THUMB_WIDTH,
            height: PDP_GALLERY_SECONDARY_THUMB_HEIGHT
          }
        }
      });
    }

    return lightboxGallery;
  }

  /**
   * PDP Only
   */
  createProductEnrichmentRules(): ProductEnrichmentRules {
    return {
      productSkus: this.sku ? [this.sku] : [],
      productUrlKeys: this.urlKey ? [this.urlKey] : [],
      productSeries: this.seriesLabel ? [this.seriesLabel] : [],
      cameraProductFamily: this.familyLabel ? [this.familyLabel] : [],
      productAccessoryCategories: this.accessoryCategory
        ? [this.accessoryCategory]
        : [],
      productCategories: this.cameraCategory ? [this.cameraCategory] : [],
      categoryIds:
        this.categories
          ?.filter(isDefined)
          .map((category) =>
            category.id != null ? String(category.id) : undefined
          )
          .filter(isDefined) ?? [],
      categoryKeys:
        this.categories
          ?.filter(isDefined)
          .map((category) => category.category_key)
          .filter(isDefined) ?? [],
      accessoryType: this.accessoryType ? [this.accessoryType] : []
    };
  }

  /**
   * PDP Only
   *
   * Values of the given attribute codes, used for enrichment matching. Only
   * codes that enrichments actually target are requested, so the enrichment
   * request stays small.
   */
  createProductEnrichmentCustomAttributes(
    attributeCodes: readonly string[]
  ): Record<string, string> {
    return Object.fromEntries(
      attributeCodes
        .map((attributeCode) => {
          const code = attributeCode.trim();
          const value = getCustomAttributeValue(
            this.mappedCustomAttributes[code]
          )?.trim();

          return code && value ? [code, value] : undefined;
        })
        .filter(isDefined)
    );
  }

  /**
   * PDP Only
   */
  getVSCLink(
    conf?: PdpConfiguration | null,
    vscPageLink?: HyperlinkInterface | null,
    selectedAccessories?: string
  ): HyperlinkInterface | undefined {
    if (!conf || !vscPageLink || !this.showVSC) {
      return;
    }
    const vscComponent = this.getVSCComponent(conf);
    if (vscComponent && this.sku) {
      return {
        ...vscPageLink,
        uri: selectedAccessories
          ? `${vscPageLink.uri}?component=${vscComponent}#sku=${this.sku}${selectedAccessories} `
          : `${vscPageLink.uri}?component=${vscComponent}#sku=${this.sku}`
      };
    }
  }

  /**
   * Parses chart data from custom_attributesV2, falling back to
   * attribute_set.entered_attribute_value when the attribute is not
   * exposed via custom_attributesV2 (Magento visibility config).
   */
  private parseChartData(attributeCode: string): [] {
    const customAttrValue = getCustomAttributeValue(
      this.mappedCustomAttributes[attributeCode]
    );

    if (customAttrValue) {
      return failSafeParseJSON<[], []>(customAttrValue, []);
    }

    const attributeSet = this.product.attribute_set?.filter(isDefined) ?? [];
    for (const group of attributeSet) {
      for (const attr of (group as AttributeSetFragment).attribute_metadata ??
        []) {
        if (
          attr?.code === attributeCode &&
          attr.entered_attribute_value?.value
        ) {
          return failSafeParseJSON<[], []>(
            attr.entered_attribute_value.value,
            []
          );
        }
      }
    }

    return [] as unknown as [];
  }

  private getVSCComponent(conf: PdpConfiguration): string | null {
    const map: { [index: string]: string } = {
      lighting: 'lighting_advisor',
      '3d_camera': 'camera_selector',
      area_scan_camera: 'camera_selector',
      line_scan_camera: 'camera_selector',
      lens: 'lens_selector',
      acquisition_cards: 'acquisition_card_selector'
    };
    const attributeSetMap = flipAttributeSetMapping(conf.attributeSets);
    const attributeSetConstant = this.product.attribute_set_id
      ? attributeSetMap[this.product.attribute_set_id]
      : null;

    if (isDefined(attributeSetConstant) && attributeSetConstant in map) {
      return map[attributeSetConstant];
    }
    return null;
  }

  public getAttributeSetName(): string | null | undefined {
    return this.product.attribute_set_name;
  }

  public async getBreadcrumbs(): Promise<NavigationItemInterface[]> {
    const { $locale } = useNuxtApp();
    const entries = await Promise.all(
      this.categories?.map(async (link) => {
        if (!link?.contentful_page_id) {
          return;
        }
        try {
          const response = await $fetch<{ slug: string }>(
            `/api/contentful/slugById/${encodeURIComponent(
              link.contentful_page_id
            )}?locale=${encodeURIComponent($locale.value)}`,
            DEFAULT_FETCH_OPTIONS
          );

          // Distributed systems: a Magento category may not map to a Contentful
          // page. If no slug resolves for the id, skip this breadcrumb segment.
          if (!response?.slug) {
            return;
          }

          return {
            text: link?.breadcrumb_name ?? link?.name ?? '',
            link: {
              uri: buildUrlString(
                $locale.value,
                response.slug,
                undefined,
                undefined,
                true
              )
            }
          };
        } catch {
          // No resolvable slug for this id (e.g. 404) → skip this segment.
          return;
        }
      }) ?? []
    );

    return entries.filter(isDefined);
  }
}

export type IndexedCustomAttributes = {
  [key: string]: CustomAttributeV2Fragment;
};

export function getIndexCustomAttributes(
  customAttributes: ReadonlyArray<CustomAttributeV2Fragment>
): IndexedCustomAttributes {
  const mappedCustomAttributes: IndexedCustomAttributes = {};
  customAttributes.filter(isDefined).forEach((attr) => {
    if (attr.code) {
      mappedCustomAttributes[attr.code] = attr;
    }
  });
  return mappedCustomAttributes;
}

const BADGE_STYLES: ReadonlySet<BadgeStyle> = new Set([
  'primary',
  'secondary',
  'danger',
  'success',
  'warning'
]);

function parseBadgeReferenceData(
  value: string | undefined,
  customAttributes: IndexedCustomAttributes,
  locale: string
): ProductBadge {
  const reference = failSafeParseJSON<BadgeReferenceData[], []>(value, [])[0];
  const badgeTextTemplate = reference?.badgetext?.value?.[0] ?? '';
  const formattedLastTimeBuy = formatBadgeDateAttribute(
    getCustomAttributeValue(customAttributes[LAST_TIME_BUY]),
    locale
  );
  const formattedLastTimeService = formatBadgeDateAttribute(
    getCustomAttributeValue(customAttributes[LAST_TIME_SERVICE]),
    locale
  );
  const comingSoonLabel =
    getCustomAttributeValue(customAttributes[COMING_SOON_LABEL]) ?? '';
  const badgeText = badgeTextTemplate
    .replaceAll(`{${LAST_TIME_BUY}}`, formattedLastTimeBuy)
    .replaceAll(`{${LAST_TIME_SERVICE}}`, formattedLastTimeService)
    .replaceAll(`{${COMING_SOON_LABEL}}`, comingSoonLabel)
    .trim();
  const configuredStyle = reference?.badgestyle?.value?.[0]?.trim();
  const badgeStyle =
    configuredStyle && BADGE_STYLES.has(configuredStyle as BadgeStyle)
      ? (configuredStyle as BadgeStyle)
      : '';

  return {
    badgeText,
    badgeStyle,
    hasShineAnimation:
      reference?.hasshineanimation?.value?.[0]?.toLowerCase() === 'true'
  };
}

function formatBadgeDateAttribute(
  value: string | undefined,
  locale: string
): string {
  if (!value) {
    return '';
  }

  return isDateString(value) || isDateTimeString(value)
    ? mapToFormattedDate(value, locale as LOCALE_CODE)
    : value;
}

function getFastDelivery(
  product:
    | (CommonProductFragment & Partial<ProductDetailFragment>)
    | (CommonProductDetailFragment & Partial<ProductDetailFragment>),
  mappedCustomAttributes: IndexedCustomAttributes
) {
  if ('variants' in product) {
    return (
      product.variants?.some((variant) => {
        if (
          variant &&
          variant?.product &&
          'custom_attributesV2' in variant.product
        ) {
          if (
            (variant.product as { stock_status?: string | null })
              .stock_status !== STATE_IN_STOCK
          ) {
            return false;
          }

          const product: ConfigurableProduct =
            variant.product as ConfigurableProduct;
          const mappedCustomAttributes = getIndexCustomAttributes(
            extractCustomAttributes(product)
          );

          return extractBool(
            getCustomAttributeValue(
              mappedCustomAttributes[FASTDELIVERY_AVAILABLE],
              {
                label: false
              }
            ),
            false
          );
        }
        return false;
      }) ?? false
    );
  } else {
    if (product.stock_status !== STATE_IN_STOCK) {
      return false;
    }

    return extractBool(
      getCustomAttributeValue(mappedCustomAttributes[FASTDELIVERY_AVAILABLE], {
        label: false
      }),
      false
    );
  }
}

function extractBool(
  attributeValue: string | undefined,
  fallback: boolean
): boolean {
  if (attributeValue) {
    return attributeValue === '1';
  }
  return fallback;
}

function extractNumber(
  attributeValue: string | undefined,
  fallback: number
): number {
  return parseInt(attributeValue ?? String(fallback), 10);
}

export class TechnicalDrawing {
  private _data:
    | {
        label?: string;
        cad_download_center_link?: string;
        technical_drawing_medialink?: string;
      }
    | null
    | undefined;

  constructor(
    data:
      | {
          label?: string;
          cad_download_center_link?: string;
          technical_drawing_medialink?: string;
        }
      | null
      | undefined
  ) {
    this._data = data;
  }

  public isValid(): boolean {
    return typeof this._data === 'object' && isDefined(this._data?.label);
  }

  public getLabel(): string | undefined {
    return this._data?.label;
  }

  public getCadLink(): string | undefined {
    return this._data?.cad_download_center_link;
  }

  public getTechnicalDrawingLink(): string | undefined {
    return this._data?.technical_drawing_medialink;
  }
}
